import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ProductChange, RecalculateResult } from "../models/ProductChange";
import {
	recalculateProductChange,
	removeProductChange,
	upsertProductChange,
} from "../repositories/ProductChangesRepository";
import { PRODUCT_CHANGES_QUERY_KEYS } from "./useProductChanges";

interface MutationOptions {
	onSuccess?: (data?: ProductChange) => void;
	onError?: (error: Error) => void;
}

export const useUpsertProductChange = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (change: ProductChange) => {
			return await upsertProductChange(change);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: PRODUCT_CHANGES_QUERY_KEYS.all,
			});
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertProductChange: mutation.mutate,
		upsertProductChangeAsync: mutation.mutateAsync,
		isUpserting: mutation.isPending,
	};
};

export const useDeleteProductChange = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (id: number) => {
			await removeProductChange(id);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: PRODUCT_CHANGES_QUERY_KEYS.all,
			});
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		deleteProductChange: mutation.mutate,
		deleteProductChangeAsync: mutation.mutateAsync,
		isDeleting: mutation.isPending,
	};
};

interface RecalculateMutationOptions {
	onSuccess?: (data: RecalculateResult) => void;
	onError?: (error: Error) => void;
}

export const useRecalculateProductChange = ({
	onSuccess,
	onError,
}: RecalculateMutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (id: number) => {
			return await recalculateProductChange(id);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: PRODUCT_CHANGES_QUERY_KEYS.all,
			});
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		recalculate: mutation.mutate,
		recalculateAsync: mutation.mutateAsync,
		isRecalculating: mutation.isPending,
	};
};
